import type { Option } from "../core/option.ts";
import type { RowId } from "./row-id.ts";
import type { RowKey } from "./row-key.ts";
import type { RowLocalId } from "./row-local-id.ts";

/**
 * The provider account that owns the shared canonical weather rows.
 * Exported so key producers (`builtin-alias-factories.ts` weather
 * templates) can emit fully-qualified owner-scoped keys at the point of
 * production (data-address migration, decision #1).
 *
 * Wire contract: must equal the server's weather provider account (its
 * sync-reset route); not a test fixture.
 */
export const WEATHER_OWNER = "weather@lona.so";

/**
 * A row key as a route spells it (`/api/rows/{rowKey}`): a string, but only
 * `RowNetworkKey.of` / `forRead` (from a `RowKey` or `RowId`) or `parse` (a
 * key already in its wire spelling) make one, so the one place a row
 * identity becomes route text — and the read shim for persisted bare
 * weather keys — is chosen at the call, not left to whoever held a string.
 */
export type RowNetworkKey = string & { readonly __brand: "RowNetworkKey" };

/**
 * Route spellings of a row key (`of`, `forRead`, `parse`) and readers of a
 * canonical row key (`:~weather:…`, `[owner]:~…`): its owner, provider and
 * the rest.
 */
export namespace RowNetworkKey {
  /** Whether `key` spells a bare canonical weather key (`:~weather:{locationId}`, no owner). */
  export function isWeatherCanonical(key: RowKey | RowId | RowNetworkKey): boolean {
    return /^:~weather:[^:@]+$/.test(key.toString());
  }

  /**
   * The explicit owner-scoped data key for a weather location:
   * `[weather@lona.so]:~weather:{locationId}`. This is the single
   * producer for fully-qualified weather keys — templates declare it via
   * `dataRef`, and no downstream layer should re-derive or prefix it.
   */
  export function weatherDataKey(locationId: string): string {
    return `[${WEATHER_OWNER}]:~weather:${locationId}`;
  }

  /**
   * `key` as a route spells it, verbatim (its canonical form): what a
   * WRITE route (`PATCH` / `DELETE /api/rows/{rowKey}`, the existence
   * check) takes. A `RowLocalId` is spelled by the wire key it addresses
   * now (`RowLocalId.toString()`).
   */
  export function of(key: RowKey | RowId | RowLocalId): RowNetworkKey {
    return mint(key.toString());
  }

  /**
   * `key` as a READ route spells it (a `RowKey` or `RowId`, or a key
   * already in its wire spelling, `fromWire`). Every live producer emits the
   * explicit owner-scoped weather key (`weatherDataKey`), which passes
   * through untouched; a PERSISTED bare `:~weather:{lid}` lookup key
   * (forked / materialized weather source rows persisted before the
   * data-address migration, Phase D) is read at its owner,
   * `[weather@lona.so]:~weather:{lid}`. The shim is read-only on purpose —
   * a write names the key it holds (`of`); removing it is gated on a
   * persisted-data audit + the Phase F live pass, and new code MUST NOT
   * rely on it.
   */
  export function forRead(key: RowKey | RowId | RowNetworkKey): RowNetworkKey {
    const raw = key.toString();
    return mint(isWeatherCanonical(key) ? `[${WEATHER_OWNER}]${raw}` : raw);
  }

  /**
   * `raw` as a route key, or `null` when it is not one (an empty string) —
   * for a key that arrives in its wire spelling (a server response's
   * `lookupKey`, a template's `dataRef`) and is sent back as is. Reads
   * that may meet a persisted bare weather key go through `forRead`.
   */
  export function parse(raw: string): Option<RowNetworkKey> {
    return raw.length > 0 ? mint(raw) : null;
  }

  /**
   * `raw`, a row key the SDK holds in its wire spelling (a row's server
   * id, a lookup key a response named, a key a caller passed as text), sent
   * verbatim — no canonicalization (a `RowKey` spells a UUID as `r_<hex>`);
   * throws when `parse` refuses it (an empty string).
   */
  export function fromWire(raw: string): RowNetworkKey {
    const key = parse(raw);
    if (key == null) throw new Error("RowNetworkKey.fromWire: an empty row key");
    return key;
  }
}

/** The brand's one mint: every constructor above decides what the text is. */
function mint(raw: string): RowNetworkKey {
  return raw as RowNetworkKey;
}
